import type { ReactNode } from "react";

import {
  type Expression,
  type Op,
  callDirectionToRoundDirection,
  getNegativeNumberLiteral,
  shouldWrapExpression,
} from "../../../utils/formula-ast";
import type { SelectedFormula } from "../hooks";

import { OperatorIcon } from "./OperatorIcon";
import { TokenDisplay } from "./TokenDisplay";

const opSymbol = (op: Op): "+" | "-" | "*" | "/" => {
  switch (op) {
    case "Add":
      return "+";
    case "Subtract":
      return "-";
    case "Multiply":
      return "*";
    case "Divide":
      return "/";
  }
};

type Props = {
  expr: Expression;
  row: number;
  selected: SelectedFormula | null;
  onSelect: (p: SelectedFormula | null) => void;
  parentOp?: Op;
  isInsideRound?: boolean;
  explicitParenKeys: ReadonlySet<string>;
  /** Stable path through the AST that uniquely identifies this node. */
  path?: string;
};

export const ExpressionDisplay = (p: Props) => {
  const { expr } = p;
  const path = p.path ?? "";

  const wrap = (content: ReactNode) => {
    if (!shouldWrapExpression(expr, p.parentOp, p.explicitParenKeys)) {
      return content;
    }
    return (
      <>
        <OperatorIcon op="(" />
        {content}
        <OperatorIcon op=")" />
      </>
    );
  };

  if ("Manual" in expr) {
    return wrap(
      <span className="text-foreground inline-flex items-center px-2 py-1 rounded-md bg-card ring-1 ring-outline text-xs font-medium">
        MANUAL
      </span>,
    );
  }

  if ("Value" in expr) {
    return wrap(
      <span className="text-foreground inline-flex items-center px-2 py-1 rounded-md bg-card ring-1 ring-outline text-lg font-medium tabular-nums">
        {expr.Value}
      </span>,
    );
  }

  if ("Variable" in expr) {
    return wrap(
      <TokenDisplay
        variable={expr.Variable}
        row={p.row}
        selected={p.selected}
        onSelect={p.onSelect}
        isInsideRound={p.isInsideRound}
        path={path}
      />,
    );
  }

  if ("UnaryOp" in expr) {
    const negativeLiteral = getNegativeNumberLiteral(expr);
    if (negativeLiteral !== undefined) {
      return (
        <span className="text-foreground inline-flex items-center px-2 py-1 rounded-md bg-card ring-1 ring-outline text-lg font-medium tabular-nums">
          {negativeLiteral}
        </span>
      );
    }

    return wrap(
      <>
        <OperatorIcon op="-" />
        <ExpressionDisplay
          expr={expr.UnaryOp}
          row={p.row}
          selected={p.selected}
          onSelect={p.onSelect}
          isInsideRound={p.isInsideRound}
          explicitParenKeys={p.explicitParenKeys}
          path={`${path}.u`}
        />
      </>,
    );
  }

  if ("BinaryOp" in expr) {
    const { lhs, op, rhs } = expr.BinaryOp;

    return wrap(
      <>
        <ExpressionDisplay
          expr={lhs}
          row={p.row}
          selected={p.selected}
          onSelect={p.onSelect}
          parentOp={op}
          isInsideRound={p.isInsideRound}
          explicitParenKeys={p.explicitParenKeys}
          path={`${path}.l`}
        />
        <OperatorIcon op={opSymbol(op)} />
        <ExpressionDisplay
          expr={rhs}
          row={p.row}
          selected={p.selected}
          onSelect={p.onSelect}
          parentOp={op}
          isInsideRound={p.isInsideRound}
          explicitParenKeys={p.explicitParenKeys}
          path={`${path}.r`}
        />
      </>,
    );
  }

  if ("Call" in expr) {
    const [funcName, args] = expr.Call;
    if (funcName === "Round" && args[0]) {
      // In Trace Precedents we don't surface the ROUND wrapper itself —
      // each affected variable inside gets a small info indicator instead.
      void (args[2] && "Value" in args[2] ? callDirectionToRoundDirection(args[2].Value) : undefined);
      return (
        <ExpressionDisplay
          expr={args[0]}
          row={p.row}
          selected={p.selected}
          onSelect={p.onSelect}
          isInsideRound={true}
          explicitParenKeys={p.explicitParenKeys}
          path={`${path}.c0`}
        />
      );
    }
    return null;
  }

  return null;
};
